import type { Chapter } from '../../types';
import { DEFAULT_CHAPTER_TARGET_WORDS } from '@xiaotianshu/shared';

/**
 * 章节「节奏」判定的共享逻辑（原 `VolumeOutline` 的独有能力，现并入章节列表的节奏视图）。
 *
 * 为什么单独成模块：这些阈值与判定此前混在 `VolumeOutline.tsx` 的组件体里，
 * 与渲染耦合、无法单测。抽出来后由 `chapterPace.test.ts` 直接锁住判定边界。
 */

/** 短章阈值（含）以下视为节奏异常过短 */
export const SHORT_CHAPTER_WORDS = 1000;
/** 长章阈值（含）以上视为节奏异常过长 */
export const LONG_CHAPTER_WORDS = 10000;
/** 未单独设置字数目标时的默认刻度（与 shared 的 DEFAULT_CHAPTER_TARGET_WORDS 一致） */
export const GOOD_CHAPTER_WORDS = DEFAULT_CHAPTER_TARGET_WORDS;
/** 进度条颜色分级：低于该字数视为不足 */
export const OK_CHAPTER_WORDS = 2000;

/** 进度条/字数颜色档位 */
export type WordTone = 'good' | 'ok' | 'low';

/** 节奏视图的筛选档 */
export type PaceFilter = 'all' | 'no-summary' | 'short' | 'long' | 'undone';

export const PACE_FILTERS: { id: PaceFilter; label: string }[] = [
  { id: 'all', label: '全部' },
  { id: 'no-summary', label: '无摘要' },
  { id: 'short', label: '短章' },
  { id: 'long', label: '长章' },
  // 长篇最常用的视图：只看还没改完的章
  { id: 'undone', label: '未完成' },
];

export function isShortChapter(ch: Chapter): boolean {
  return (ch.word_count ?? 0) <= SHORT_CHAPTER_WORDS;
}

export function isLongChapter(ch: Chapter): boolean {
  return (ch.word_count ?? 0) >= LONG_CHAPTER_WORDS;
}

export function hasSummary(ch: Chapter): boolean {
  return !!ch.summary && ch.summary.trim().length > 0;
}

export function isUndoneChapter(ch: Chapter): boolean {
  return (ch.status ?? 'draft') !== 'done';
}

/** 单章进度条刻度：优先该章自己的字数目标，未设置时回落默认刻度 */
export function chapterScale(ch: Chapter): number {
  return ch.target_words && ch.target_words > 0 ? ch.target_words : GOOD_CHAPTER_WORDS;
}

/** 进度百分比（0-100，按该章刻度封顶） */
export function chapterProgressPct(ch: Chapter): number {
  return Math.min(100, Math.round(((ch.word_count ?? 0) / chapterScale(ch)) * 100));
}

/** 该章是否已达自己的字数目标（仅在显式设置目标时才算"达标"） */
export function isTargetMet(ch: Chapter): boolean {
  return (ch.target_words ?? 0) > 0 && (ch.word_count ?? 0) >= (ch.target_words ?? 0);
}

/**
 * 字数颜色档位。
 *
 * 有明确目标的章：以目标为准（达标=good，不足 2/3=low）；
 * 没有目标时用固定刻度（短章/长章的判定是"跨章对比节奏"用的相对刻度，
 * 与某一章的字数目标无关，故仍用固定阈值）。
 */
export function wordTone(ch: Chapter): WordTone {
  const count = ch.word_count ?? 0;
  const target = ch.target_words ?? 0;
  if (target > 0) {
    if (count >= target) return 'good';
    if (count < (target * 2) / 3) return 'low';
    return 'ok';
  }
  if (count >= GOOD_CHAPTER_WORDS) return 'good';
  if (count < OK_CHAPTER_WORDS) return 'low';
  return 'ok';
}

export function matchPaceFilter(ch: Chapter, filter: PaceFilter): boolean {
  switch (filter) {
    case 'no-summary': return !hasSummary(ch);
    case 'short': return isShortChapter(ch);
    case 'long': return isLongChapter(ch);
    case 'undone': return isUndoneChapter(ch);
    default: return true;
  }
}

/** 各筛选档的命中数量（用于筛选按钮上的角标） */
export function paceFilterCounts(chapters: Chapter[]): Record<PaceFilter, number> {
  const counts: Record<PaceFilter, number> = {
    all: chapters.length, 'no-summary': 0, short: 0, long: 0, undone: 0,
  };
  for (const ch of chapters) {
    if (!hasSummary(ch)) counts['no-summary']++;
    if (isShortChapter(ch)) counts.short++;
    if (isLongChapter(ch)) counts.long++;
    if (isUndoneChapter(ch)) counts.undone++;
  }
  return counts;
}

/**
 * 去掉 HTML 标签取纯文本（摘要可能含富文本）。
 *
 * 刻意**不依赖 DOM**：本模块由 vitest 在 node 环境直接单测，
 * 用 `document.createElement` 会直接抛 `document is not defined`。
 * 做法：先给块级闭合标签补空格（避免 `<p>甲</p><p>乙</p>` 粘成"甲乙"），
 * 再剥标签、解实体、压缩空白。
 */
export function plainSummary(html: string): string {
  if (!html) return '';
  const text = html
    .replace(/<\/(p|div|li|h[1-6]|blockquote|tr)>/gi, ' ')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&amp;/gi, '&');
  return text.replace(/\s+/g, ' ').trim();
}
